import { Button } from "primereact/button";
import { IDropdown, IRoomType } from "../../../models/BaseModel";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { Fetch } from "../../../shared/Fetch";
import { JVInput } from "../../../shared/JVInput";
import { JVCheckbox } from "../../../shared/JVCheckbox";
import { JVDropdown } from "../../../shared/JVDropdown";
import { JVTextArea } from "../../../shared/JVTextarea";
import { JVMultiselect } from "../../../shared/JVMultiselect";
import { Badge } from "primereact/badge";

const schema = yup.object({
    hotelId: yup.number().required('Hotel is required').min(1, 'Hotel is required'),
    name: yup.string().required("Room Type is required"),
    capacity: yup.number().typeError("Capacity is required").required("Capacity is required").min(1, "Capacity is required"),
    mattress: yup.number().typeError("Allowed Extra Mattress is required").required("Allowed Extra Mattress is required").min(0, "Allowed Extra Mattress is required"),
    mattressPrice: yup.number().typeError("Mattress Price/Day is required").required("Mattress Price/Day is required").min(0, "Mattress Price/Day is required"),
    roomGlobalTypeId: yup.number().required('BedType is required').min(1, 'BedType is required'),
    availableRooms: yup.number().typeError('Room count is required').required('Room count is required').min(1, 'Room count is requied'),
    desc: yup.string().nullable(),
});

interface Iprops {
    singleData: IRoomType,
    onSuccess: () => void,
    onClose: () => void,
    toast: any
}

export const RoomTypeForm = (props: Iprops) => {
    const [submitting, setSubmitting] = useState(false);
    const [beds, setBeds] = useState<{ bedTypeId: number, quantity: number }[]>();
    const [bedTypes, setBedTypes] = useState<IDropdown[]>();
    const [roomgtypes, setRoomgtypes] = useState<IDropdown[]>();

    const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<IRoomType>({
        resolver: yupResolver(schema),
        defaultValues: props.singleData,
        shouldFocusError: false,
    });

    const mattress = +(watch('mattress') ?? 0);

    useEffect(() => {
        if (mattress === 0) {
            setValue('mattressPrice', 0);
        }
    }, [mattress]);

    function addCountBed(bedTypeId: number) {
        const updated = beds?.map(b =>
            b.bedTypeId === bedTypeId ? { ...b, quantity: b.quantity + 1 } : b
        );
        setBeds(updated);
    }
    function removeCountBed(bedTypeId: number) {
        const updated = beds?.map(b =>
            b.bedTypeId === bedTypeId ? { ...b, quantity: b.quantity - 1 } : b)
            .filter(r => r.quantity > 0);
        setBeds(updated);
    }

    const onBedsChange = (newBeds: any[]) => {
        setBeds(prev => {
            return newBeds.map((b: any) => {
                const existing = prev?.find(p => p.bedTypeId === b.id);
                const quantity = existing?.quantity && existing.quantity > 0 ? existing.quantity : 1;
                return { bedTypeId: b.id, quantity: quantity };
            });
        });
    };

    useEffect(() => {
        reset(props.singleData);
        if (props.singleData.id) {
            setBeds(props.singleData.roomTypeBedTypes?.map(rb => ({ bedTypeId: rb.bedTypeId, quantity: rb.quantity })));
        }
    }, [props.singleData]);


    const loadBedTypes = async () => {
        const response = await Fetch(`bedtype/dropdown`, "GET", null, null, null);
        if (response) {
            setBedTypes(await response.json());
        }
    }
    const loadRoomgypes = async () => {
        const response = await Fetch(`roomGlobalType/dropdown`, "GET", null, null, null);
        if (response) {
            setRoomgtypes(await response.json());
        }
    }
    useEffect(() => {
        loadBedTypes();
        loadRoomgypes();
    }, []);

    const saveToDB = async (body: IRoomType) => {
        setSubmitting(true);
        const method = props.singleData.id ? "PUT" : "POST";
        const message = props.singleData.id ? "Updated Successfully" : "Added Successfully";

        const response = await Fetch(`roomtype${props.singleData.id ? '/' + props.singleData.id : ''}`, method, body, null, props.toast);
        if (response) {
            props.toast.current.show({ severity: 'success', detail: message, life: 3000 });
            props.onSuccess();
        }
        setSubmitting(false);
    }

    function onSubmit(data: any) {
        if (beds === undefined || beds.length < 1) {
            props.toast.current.show({ severity: 'error', detail: 'Select Bed Type', life: 3000 });
            return;
        }

        data.roomTypeBedTypes = beds?.map(s => ({ bedTypeId: s.bedTypeId, quantity: s.quantity }));
        saveToDB(data);
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
                <JVInput column={2} required label="Room Type Name" control={control} name="name" error={errors.name?.message} />
                <JVDropdown column={2} required control={control} label="Room Category" options={roomgtypes} optionLabel="name" optionValue="id" name="roomGlobalTypeId" error={errors.roomGlobalTypeId?.message} />
                <JVInput column={2} type="number" required label="Occupancy" control={control} name="capacity" error={errors.capacity?.message} />
                <JVInput column={2} type="number" required label="Room Count" control={control} name="availableRooms" error={errors.availableRooms?.message} />
                <JVInput column={2} type="number" required label="Extra Mattress" control={control} name="mattress" error={errors.mattress?.message} />
                {mattress > 0 && <JVInput column={2} type="number" required label="Mattress Price/Day" control={control} name="mattressPrice" error={errors.mattressPrice?.message} />}

                <JVMultiselect required column={1} label="Bed Types" options={bedTypes} optionLabel="name" placeholder="Select BedTypes" value={bedTypes?.filter(bt => beds?.map(e => e.bedTypeId).includes(bt.id))} onChange={e => onBedsChange(e)} />
                <div className="field col-12 flex flex-wrap gap-2">
                    {beds?.map((b) => <div key={b.bedTypeId} className="flex items-center">
                        <div className="card flex-1 p-0" style={{ width: '250px', position: 'relative', backgroundImage: 'linear-gradient(to bottom, #ffffff, #f0f0f0)' }}>
                            <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', padding: '10px' }}>
                                <Badge value={b.quantity} severity="warning"></Badge>
                                <label className="ml-2">{bedTypes?.find(e => e.id === b.bedTypeId)?.name}</label>
                            </div>
                            <div className="flex" style={{ position: 'absolute', bottom: 0, right: 0 }}>
                                <Button style={{ height: '20px', width: '20px', margin: '5px' }} onClick={() => removeCountBed(b.bedTypeId)} icon="pi pi-minus" type="button" className="p-button-danger" />
                                <Button style={{ height: '20px', width: '20px', margin: '5px' }} onClick={() => addCountBed(b.bedTypeId)} icon="pi pi-plus" type="button" className="p-button-success" />
                            </div>
                        </div>
                    </div>)}
                </div>
                <JVTextArea column={1} label="Description" control={control} name="desc" error={errors.desc?.message} />
                {props.singleData.id && <JVCheckbox label="Status" control={control} name="isActive" />}

                <div className="col-12 md:col-12 flex gap-4 justify-content-end mt-2">
                    <Button style={{ width: '100px' }} loading={submitting} label="SAVE" type="submit" className="p-button-rounded"></Button>
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={props.onClose} type="button" className="p-button-rounded p-button-outlined surface-200"></Button>
                </div>
            </form>
        </>
    );
}